import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

const ROLES = [
  { id: 'student', name: '교육생', description: '강의 출석 및 QR 스캔 기능에 접근할 수 있습니다.' },
  { id: 'instructor', name: '강사', description: '강의 관리 및 출석 보고서에 접근할 수 있습니다.' },
  { id: 'admin', name: '관리자', description: '시스템의 모든 기능에 접근할 수 있습니다.' },
];

const UserManagement = () => {
  const { authAxios } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRoles, setUserRoles] = useState({
    student: false,
    instructor: false,
    admin: false
  });
  const [activeTab, setActiveTab] = useState('student'); // 'instructor', 'student', 'admin'
  
  // 교육생 관리를 위한 상태 추가
  const [students, setStudents] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [studentError, setStudentError] = useState(null);
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [isEditingStudent, setIsEditingStudent] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  // 교육생 강의 목록을 위한 상태 추가
  const [studentClasses, setStudentClasses] = useState({});
  const [loadingClasses, setLoadingClasses] = useState({});
  // 강의 등록 팝업을 위한 상태 추가
  const [showClassModal, setShowClassModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [allClasses, setAllClasses] = useState([]);
  const [loadingAllClasses, setLoadingAllClasses] = useState(false);
  const [selectedAvailableClasses, setSelectedAvailableClasses] = useState([]);
  const [selectedEnrolledClasses, setSelectedEnrolledClasses] = useState([]);

  // 인라인 편집 상태 추가
  const [editingRow, setEditingRow] = useState(null);
  const [editingStudentRow, setEditingStudentRow] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    username: '',
    email: '',
    roles: []
  });
  const [editStudentFormData, setEditStudentFormData] = useState({
    name: '',
    phone: '',
    note: '',
    batch: '',
  });
  
  // 기수별 탭을 위한 상태 추가
  const [batchTab, setBatchTab] = useState('all');
  const [availableBatches, setAvailableBatches] = useState([]);
  
  // 정렬 및 필터링 관련 상태 추가
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  // 회원 목록 가져오기
  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // API 호출
      const response = await authAxios.get('/users');
      
      // 응답 데이터 확인 및 필요시 사용자 객체 형식 조정
      const userData = response.data;
      console.log('서버에서 받은 사용자 데이터:', userData);
      
      // 각 사용자 객체에 id 필드 확인 및 추가
      const processedUsers = userData.map(user => {
        if (!user.id && (user._id || user.userId || user.user_id)) {
          return {...user, id: user._id || user.userId || user.user_id};
        }
        return user;
      });
      
      setUsers(processedUsers);
      setIsLoading(false);
    } catch (err) {
      setError('회원 목록을 불러오는 중 오류가 발생했습니다.');
      setIsLoading(false);
      console.error('Users list error:', err);
    }
  };

  // 교육생 목록 가져오기
  const fetchStudents = async () => {
    try {
      setIsLoadingStudents(true);
      setStudentError(null);
      
      // 교육생 API 호출
      const response = await authAxios.get('students');
      
      // 응답 데이터 확인
      const studentData = response.data;
      console.log('서버에서 받은 교육생 데이터:', studentData);
      
      // 각 교육생 객체에 id 필드 확인
      const processedStudents = studentData.map(student => {
        if (!student.id && (student._id || student.studentId)) {
          return {...student, id: student._id || student.studentId};
        }
        return student;
      });
      
      setStudents(processedStudents);
      setIsLoadingStudents(false);
      
      // 기수 정보 추출하여 탭 생성에 사용
      const batches = [...new Set(processedStudents.map(student => student.batch))]
        .filter(batch => batch !== undefined && batch !== null)
        .sort((a, b) => a - b);
      setAvailableBatches(batches);
      
      // 각 교육생의 강의 목록 가져오기
      processedStudents.forEach(student => {
        fetchStudentClasses(student.id);
      });
    } catch (err) {
      setStudentError('교육생 목록을 불러오는 중 오류가 발생했습니다.');
      setIsLoadingStudents(false);
      console.error('Students list error:', err);
    }
  };
  
  // 교육생별 강의 목록 가져오기
  const fetchStudentClasses = async (studentId) => {
    try {
      setLoadingClasses(prev => ({ ...prev, [studentId]: true }));
      
      // 이 API 경로가 올바른지 확인하세요: 
      // '/api/students/{studentId}/classes' 또는 '/api/classes/student/{studentId}'
      const response = await authAxios.get(`/students/${studentId}/classes`);
      
      // 응답 데이터 형식 확인
      console.log('강의 목록 응답:', response);
      console.log('강의 목록 데이터:', response.data);
      
      setStudentClasses(prev => ({
        ...prev,
        [studentId]: Array.isArray(response.data) ? response.data.map(c => ({
          ...c,
          id: c._id || c.id
        })) : []
      }));
    } catch (error) {
      console.error('학생 강의 목록 조회 오류:', error);
      // 오류 세부 정보 로깅
      if (error.response) {
        console.error('응답 상태:', error.response.status);
        console.error('응답 데이터:', error.response.data);
      }
      
      // 에러 발생 시 빈 배열 설정
      setStudentClasses(prev => ({
        ...prev,
        [studentId]: []
      }));
    } finally {
      setLoadingClasses(prev => ({ ...prev, [studentId]: false }));
    }
  };

  // 모든 강의 목록 가져오기
  const fetchAllClasses = async () => {
    try {
      setLoadingAllClasses(true);
      // API 경로 수정: /classes -> /api/classes
      const response = await authAxios.get('/classes');
      
      // 응답 데이터 구조 디버깅
      console.log('강의 목록 응답:', response.data);
      
      const classesData = response.data.map(classItem => ({
        ...classItem,
        id: classItem._id || classItem.id,
        category: classItem.category || '일반',
        structure: classItem.structure || '이론'
      }));
      setAllClasses(classesData);
      setLoadingAllClasses(false);
    } catch (err) {
      console.error('강의 목록 조회 오류:', err);
      console.error('오류 상세 정보:', err.response?.data || err.message);
      setLoadingAllClasses(false);
      
      // 임시 데이터 사용 (테스트용)
      setAllClasses([
        { id: '1', name: '자바 프로그래밍', category: '프로그래밍', structure: '이론+실습' },
        { id: '2', name: '웹 개발 기초', category: '웹', structure: '이론' },
        { id: '3', name: '데이터베이스 입문', category: '데이터베이스', structure: '실습' }
      ]);
    }
  };

  // 컴포넌트 마운트 시 데이터 로딩
  useEffect(() => {
    fetchUsers();
    fetchStudents();
    fetchAllClasses(); // 모든 강의 목록도 함께 로드
  }, []);

  // 역할 초기화
  const resetRoles = () => {
    setUserRoles({
      student: false,
      instructor: false,
      admin: false
    });
  };

  // 회원 생성 핸들러
  const onAddUser = async (data) => {
    try {
      // 선택된 역할 정보 가져오기
      const selectedRoles = Object.keys(userRoles).filter(role => userRoles[role]);
      
      if (selectedRoles.length === 0) {
        alert('최소한 하나의 역할을 선택해야 합니다.');
        return;
      }

      console.log('회원 추가 전송 데이터:', data);

      // API 호출
      const userData = {
        ...data,
        roles: selectedRoles,
        // 서버가 요구할 수 있는 추가 필드들
        email: data.email || `${data.username}@example.com`, // 이메일 필드 기본값 설정
        active: true // 계정 활성화 상태
      };

      console.log('서버로 전송할 데이터:', userData);
      
      const response = await authAxios.post('/users', userData);
      console.log('서버 응답:', response.data);
      
      // 회원 목록 업데이트
      setUsers([...users, response.data]);
      setIsAddingUser(false);
      reset();
      resetRoles();
    } catch (err) {
      console.error('Add user error:', err);
      
      // 응답 상세 정보 확인
      if (err.response) {
        console.error('서버 응답 상태:', err.response.status);
        console.error('서버 응답 데이터:', err.response.data);
        
        // 중복 사용자명 오류 처리 (MongoDB E11000 error)
        if (err.response.data && err.response.data.message && 
            (err.response.data.message.includes('E11000') || 
             err.response.data.message.includes('duplicate key') || 
             err.response.data.message.includes('dup key'))) {
          alert(`이미 사용 중인 사용자명입니다. 다른 사용자명을 입력해주세요.`);
          return;
        }
        
        // 서버에서 구체적인 오류 메시지를 제공하는 경우
        if (err.response.data && err.response.data.message) {
          alert(`회원 추가 중 오류: ${err.response.data.message}`);
        } else {
          alert('회원 추가 중 오류가 발생했습니다. 서버 응답: ' + err.response.status);
        }
      } else {
        alert('회원 추가 중 오류가 발생했습니다.');
      }
    }
  };

  // 회원 수정 핸들러
  const onEditUser = async (data) => {
    try {
      // 선택된 역할 정보 가져오기
      const selectedRoles = Object.keys(userRoles).filter(role => userRoles[role]);
      
      if (selectedRoles.length === 0) {
        alert('최소한 하나의 역할을 선택해야 합니다.');
        return;
      }

      if (!currentUser || !currentUser.id) {
        console.error('현재 사용자 ID가 없습니다:', currentUser);
        alert('사용자 정보가 올바르지 않습니다. 다시 시도해주세요.');
        return;
      }

      console.log('수정할 사용자 정보:', currentUser);

      // API 호출
      const userData = {
        ...data,
        roles: selectedRoles
      };
      const response = await authAxios.put(`/users/${currentUser.id}`, userData);
      
      // 회원 목록 업데이트
      const updatedUsers = users.map(user => 
        user.id === currentUser.id ? response.data : user
      );
      
      setUsers(updatedUsers);
      setIsEditingUser(false);
      setCurrentUser(null);
      reset();
      resetRoles();
    } catch (err) {
      console.error('Edit user error:', err);
      alert('회원 수정 중 오류가 발생했습니다.');
    }
  };

  // 회원 삭제 핸들러
  const onDeleteUser = async (id) => {
    if (!window.confirm('정말로 이 회원을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    
    try {
      // API 호출
      await authAxios.delete(`/users/${id}`);
      
      // 회원 목록 업데이트
      const filteredUsers = users.filter(user => user.id !== id);
      setUsers(filteredUsers);
    } catch (err) {
      console.error('Delete user error:', err);
      alert('회원 삭제 중 오류가 발생했습니다.');
    }
  };

  // 패스워드 초기화 핸들러
  const onResetPassword = async (data) => {
    try {
      // API 호출
      await authAxios.post(`/users/${currentUser.id}/reset-password`, {
        newPassword: data.newPassword
      });
      
      alert('비밀번호가 성공적으로 변경되었습니다.');
      setIsResettingPassword(false);
      setCurrentUser(null);
      reset();
    } catch (err) {
      console.error('Password reset error:', err);
      alert('비밀번호 재설정 중 오류가 발생했습니다.');
    }
  };

  // 수정 모드 시작
  const startEditUser = (user) => {
    console.log('수정을 위한 사용자 데이터:', user);
    
    // ID 필드가 없는 경우를 확인
    if (!user.id) {
      console.error('사용자 객체에 ID가 없습니다:', user);
      // id, _id, userId 등 다른 가능한 필드 확인
      const possibleId = user._id || user.userId || user.user_id;
      if (possibleId) {
        user = {...user, id: possibleId};
        console.log('ID 필드를 추가했습니다:', user);
      } else {
        alert('사용자 정보에 ID가 없어 수정할 수 없습니다.');
        return;
      }
    }
    
    setCurrentUser(user);
    setValue('name', user.name);
    
    // 역할 체크박스 설정
    resetRoles();
    user.roles.forEach(role => {
      setUserRoles(prev => ({ ...prev, [role]: true }));
    });
    
    setIsEditingUser(true);
  };

  // 비밀번호 재설정 모드 시작
  const startResetPassword = (user) => {
    setCurrentUser(user);
    reset();
    setIsResettingPassword(true);
  };

  // 역할에 따른 배지 색상
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'instructor':
        return 'bg-yellow-100 text-yellow-800';
      case 'student':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 역할 표시 한글화
  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin':
        return '관리자';
      case 'instructor':
        return '강사';
      case 'student':
        return '교육생';
      default:
        return role;
    }
  };

  // 필터링된 회원 목록 가져오기
  const getFilteredUsers = () => {
    return users.filter(user => user.roles.includes(activeTab));
  };

  // 교육생 추가 핸들러
  const onAddStudent = async (data) => {
    try {
      console.log('교육생 추가 전송 데이터:', data);
      
      // 기수(batch) 값이 숫자로 전달되는지 확인
      const batch = typeof data.batch === 'number' ? data.batch : 
        typeof data.batch === 'string' ? parseInt(data.batch.replace(/[^0-9]/g, '')) || 1 : 1;
      
      const studentData = {
        name: data.name,
        phone: data.phone,
        email: data.email || `${data.phone}@example.com`,
        gender: data.gender || '남',
        birthDate: data.birthDate || '2000-01-01',
        address: data.address || '',
        batch: batch,
        note: data.note || ''
      };
      
      console.log('서버로 전송할 데이터:', studentData);
      
      const response = await authAxios.post('students', studentData);
      console.log('서버 응답:', response.data);
      
      // 교육생 목록 업데이트
      setStudents([...students, response.data]);
      setIsAddingStudent(false);
      reset();
      
      // 성공 메시지 표시
      toast.success(`${data.name} 교육생이 ${batch}기로 추가되었습니다.`);
    } catch (err) {
      console.error('교육생 추가 오류:', err);
      
      if (err.response) {
        console.error('서버 응답 상태:', err.response.status);
        console.error('서버 응답 데이터:', err.response.data);
        
        if (err.response.data && err.response.data.message) {
          toast.error(`교육생 추가 중 오류: ${err.response.data.message}`);
        } else {
          toast.error('교육생 추가 중 오류가 발생했습니다. 서버 응답: ' + err.response.status);
        }
      } else {
        toast.error('교육생 추가 중 오류가 발생했습니다.');
      }
    }
  };
  
  // 교육생 수정 핸들러
  const onEditStudent = async (data) => {
    try {
      if (!currentStudent || !currentStudent.id) {
        console.error('현재 교육생 ID가 없습니다:', currentStudent);
        toast.error('교육생 정보가 올바르지 않습니다. 다시 시도해주세요.');
        return;
      }
      
      console.log('수정할 교육생 정보:', currentStudent);
      
      // 기수(batch) 값이 숫자로 전달되는지 확인
      const batch = typeof data.batch === 'number' ? data.batch : 
        typeof data.batch === 'string' ? parseInt(data.batch.replace(/[^0-9]/g, '')) || 1 : 1;
      
      // API 호출
      const studentData = {
        name: data.name,
        phone: data.phone,
        email: data.email || currentStudent.email,
        gender: data.gender || currentStudent.gender || '남',
        birthDate: data.birthDate || currentStudent.birthDate || '2000-01-01',
        address: data.address || currentStudent.address || '',
        batch: batch,
        note: data.note || ''
      };
      
      const response = await authAxios.put(`students/${currentStudent.id}`, studentData);
      
      // 교육생 목록 업데이트
      const updatedStudents = students.map(student => 
        student.id === currentStudent.id ? response.data : student
      );
      
      setStudents(updatedStudents);
      setIsEditingStudent(false);
      setCurrentStudent(null);
      reset();
      
      // 성공 메시지 표시
      toast.success(`${data.name} 교육생 정보가 업데이트되었습니다. (${batch}기)`);
    } catch (err) {
      console.error('교육생 수정 오류:', err);
      toast.error('교육생 수정 중 오류가 발생했습니다.');
    }
  };
  
  // 교육생 삭제 핸들러
  const onDeleteStudent = async (id) => {
    if (!window.confirm('정말로 이 교육생을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    
    try {
      // API 호출
      await authAxios.delete(`students/${id}`);
      
      // 교육생 목록 업데이트
      const filteredStudents = students.filter(student => student.id !== id);
      setStudents(filteredStudents);
    } catch (err) {
      console.error('교육생 삭제 오류:', err);
      alert('교육생 삭제 중 오류가 발생했습니다.');
    }
  };
  
  // 교육생 수정 모드 시작
  const startEditStudent = (student) => {
    if (!student.id) {
      const possibleId = student._id || student.studentId;
      if (possibleId) {
        student = {...student, id: possibleId};
      } else {
        toast.error('교육생 정보에 ID가 없어 수정할 수 없습니다.');
        return;
      }
    }
    
    setCurrentStudent(student);
    
    // 기수(batch) 값이 숫자 또는 문자열인지 확인하고 적절히 변환
    let batchValue = student.batch;
    if (typeof batchValue === 'string') {
      // '1기'와 같은 형식에서 숫자만 추출
      batchValue = parseInt(batchValue.replace(/[^0-9]/g, ''));
    }
    // 숫자가 아니거나 유효하지 않은 값이면 기본값 1로 설정
    if (isNaN(batchValue) || batchValue <= 0) {
      batchValue = 1;
    }
    
    setValue('name', student.name);
    setValue('phone', student.phone);
    setValue('email', student.email);
    setValue('gender', student.gender);
    setValue('birthDate', student.birthDate);
    setValue('address', student.address);
    setValue('batch', batchValue);
    setValue('category', student.category || '단기');
    setValue('note', student.note || '');
    
    setIsEditingStudent(true);
  };

  // 인라인 편집 시작 (회원)
  const startInlineEdit = (user) => {
    setEditingRow(user.id);
    setEditFormData({
      name: user.name,
      username: user.username,
      email: user.email || '',
      roles: user.roles
    });
  };

  // 인라인 편집 시작 (교육생)
  const startInlineEditStudent = (student) => {
    setEditingStudentRow(student.id);
    setEditStudentFormData({
      name: student.name,
      phone: student.phone,
      email: student.email,
      gender: student.gender,
      birthDate: student.birthDate,
      address: student.address,
      batch: student.batch || '1기',
      note: student.note || ''
    });
  };
  
  // 인라인 편집 취소
  const cancelInlineEdit = () => {
    setEditingRow(null);
    setEditFormData({
      name: '',
      username: '',
      email: '',
      roles: []
    });
  };
  
  // 인라인 편집 취소 (교육생)
  const cancelInlineEditStudent = () => {
    setEditingStudentRow(null);
    setEditStudentFormData({
      name: '',
      phone: '',
      note: '',
      batch: '',
    });
  };
  
  // 인라인 변경 핸들러 (회원)
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };
  
  // 인라인 변경 핸들러 (교육생)
  const handleEditStudentFormChange = (e) => {
    const { name, value } = e.target;
    setEditStudentFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // 인라인 필드 수정 핸들러 (교육생)
  const handleInlineFieldUpdate = async (studentId, field, value) => {
    try {
      console.log(`인라인 수정 시도: ${field} 필드를 ${value}로 변경합니다.`);
      
      // 유효성 검사
      if (field === 'name' && (!value || value.trim() === '')) {
        toast.error('이름은 비워둘 수 없습니다.');
        return false;
      }
      
      if (field === 'phone' && (!value || value.trim() === '')) {
        toast.error('연락처는 비워둘 수 없습니다.');
        return false;
      }
      
      // 기수 필드 처리
      let formattedValue = value;
      if (field === 'batch') {
        const batchValue = typeof value === 'string' 
          ? parseInt(value.replace(/[^0-9]/g, '')) || 1 
          : value;
        formattedValue = batchValue;
      }
      
      // 교육생 정보 찾기
      const student = students.find(s => s.id === studentId);
      if (!student) {
        toast.error('교육생 정보를 찾을 수 없습니다.');
        return false;
      }
      
      let response;
      
      // 새 교육생인 경우 (id가 'new-'로 시작하는 경우)
      if (String(studentId).startsWith('new-')) {
        // 필수 항목 유효성 검사
        const name = field === 'name' ? value : student.name;
        const phone = field === 'phone' ? value : student.phone;
        
        if (!name || name.trim() === '') {
          toast.error('이름은 필수 항목입니다.');
          return false;
        }
        
        if (!phone || phone.trim() === '') {
          toast.error('연락처는 필수 항목입니다.');
          return false;
        }
        
        // 새 교육생 등록에 필요한 최소 데이터 준비
        const newStudentData = {
          name: name,
          phone: phone,
          batch: field === 'batch' ? formattedValue : (student.batch || 1)
        };
        
        console.log('새 교육생 등록 데이터:', newStudentData);
        
        // POST 요청으로 새 교육생 등록
        response = await authAxios.post('students', newStudentData);
        console.log('새 교육생 등록 응답:', response.data);
        
        // 목록에서 임시 ID를 가진 항목 제거하고 새로 받은 데이터 추가
        const newStudent = response.data;
        const updatedStudents = students.filter(s => s.id !== studentId);
        updatedStudents.push({
          ...newStudent,
          id: newStudent._id || newStudent.id
        });
        
        setStudents(updatedStudents);
        
        // 편집 모드 종료
        setEditingStudentRow(null);
        
        toast.success('새 교육생이 등록되었습니다.');
      } 
      // 기존 교육생 수정
      else {
        // 수정 데이터 준비
        const updateData = {
          ...student,
          [field]: formattedValue
        };
        
        // 필수 항목 확인
        if (!updateData.name || updateData.name.trim() === '') {
          toast.error('이름은 필수 항목입니다.');
          return false;
        }
        
        if (!updateData.phone || updateData.phone.trim() === '') {
          toast.error('연락처는 필수 항목입니다.');
          return false;
        }
        
        // PUT 메서드로 변경
        response = await authAxios.put(`students/${studentId}`, updateData);
        
        // 성공한 경우 로컬 상태 업데이트
        const updatedStudents = students.map(s => {
          if (s.id === studentId) {
            return { ...s, [field]: formattedValue };
          }
          return s;
        });
        
        setStudents(updatedStudents);
      }
      
      console.log(`${field} 수정 완료:`, response.data);
      return true;
    } catch (err) {
      console.error('인라인 수정 오류:', err);
      
      // 오류 메시지 표시
      if (err.response) {
        const errorData = err.response.data;
        let errorMessage = '정보 수정 중 오류가 발생했습니다.';
        
        // NestJS 오류 메시지 형식 처리
        if (errorData?.message) {
          if (Array.isArray(errorData.message)) {
            errorMessage = errorData.message.join(', ');
          } else {
            errorMessage = errorData.message;
          }
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        }
        
        toast.error(errorMessage);
        console.error('서버 오류 응답:', errorData);
      } else {
        toast.error(`정보 수정 중 오류: ${err.message}`);
      }
      return false;
    }
  };
  
  // 인라인 업데이트 핸들러 (회원)
  const handleInlineUpdate = async (userId) => {
    try {
      if (editFormData.roles.length === 0) {
        alert('최소한 하나의 역할을 선택해야 합니다.');
        return;
      }
      
      // 필수 필드 검증
      if (!editFormData.name) {
        alert('이름은 필수입니다.');
        return;
      }
      
      // 새 회원인 경우(id가 'new'로 시작하는 경우)
      if (String(userId).startsWith('new')) {
        // 사용자 이름(아이디) 필수 검사
        if (!editFormData.username) {
          alert('회원 ID는 필수입니다.');
          return;
        }
        
        // 사용자 이름 형식 검사
        if (!/^[a-zA-Z0-9_]{4,20}$/.test(editFormData.username)) {
          alert('회원 ID는 영문, 숫자, 밑줄(_)만 포함하고 4~20자여야 합니다.');
          return;
        }
        
        // 기본 비밀번호 생성
        const defaultPassword = `${editFormData.username}1234`;
        
        // API 호출 - 새 회원 추가
        const userData = {
          username: editFormData.username,
          password: defaultPassword,
          name: editFormData.name,
          email: editFormData.email || `${editFormData.username}@example.com`,
          roles: editFormData.roles,
          active: true
        };
        
        console.log('새 회원 등록 데이터:', userData);
        
        const response = await authAxios.post('/users', userData);
        console.log('새 회원 등록 응답:', response.data);
        
        // 회원 목록 업데이트
        setUsers(prev => [
          ...prev.filter(user => user.id !== userId), 
          {...response.data, id: response.data._id || response.data.id}
        ]);
        
        alert(`회원이 추가되었습니다. 기본 비밀번호는 ${defaultPassword} 입니다.`);
      } else {
        // 기존 회원 수정
        const userData = {
          name: editFormData.name,
          roles: editFormData.roles
        };
        
        // 이메일이 있으면 추가
        if (editFormData.email) {
          userData.email = editFormData.email;
        }
        
        console.log('회원 정보 수정 데이터:', userData);
        
        const response = await authAxios.put(`/users/${userId}`, userData);
        console.log('회원 정보 수정 응답:', response.data);
        
        // 회원 목록 업데이트
        const updatedUsers = users.map(user => 
          user.id === userId ? { ...user, ...response.data } : user
        );
        
        setUsers(updatedUsers);
      }
      
      // 편집 모드 종료
      setEditingRow(null);
      
    } catch (err) {
      console.error('Inline update error:', err);
      
      // 서버 응답 상세 정보 로깅
      if (err.response) {
        console.error('서버 응답 상태:', err.response.status);
        console.error('서버 응답 데이터:', err.response.data);
        
        // 중복 사용자명 오류 처리 (MongoDB E11000 error)
        if (err.response.data && err.response.data.message && 
            (err.response.data.message.includes('E11000') || 
            err.response.data.message.includes('duplicate key') || 
            err.response.data.message.includes('dup key'))) {
          alert(`이미 사용 중인 사용자명입니다. 다른 사용자명을 입력해주세요.`);
          return;
        }
        
        // 서버에서 구체적인 오류 메시지를 제공하는 경우
        if (err.response.data && err.response.data.message) {
          alert(`회원 정보 수정 오류: ${err.response.data.message}`);
        } else {
          alert('회원 정보 수정 중 오류가 발생했습니다. 서버 응답: ' + err.response.status);
        }
      } else {
        alert('회원 정보 수정 중 오류가 발생했습니다.');
      }
    }
  };
  
  // 인라인 업데이트 핸들러 (교육생) - 수정
  const handleInlineUpdateStudent = async (studentId) => {
    try {
      // 필수 필드 검증
      if (!editStudentFormData.name) {
        toast.error('이름은 필수입니다.');
        return;
      }
      if (!editStudentFormData.phone) {
        toast.error('연락처는 필수입니다.');
        return;
      }
      
      // 백엔드에 전송할 데이터 준비
      const studentData = {
        name: editStudentFormData.name,
        phone: editStudentFormData.phone,
        note: editStudentFormData.note || ''
      };
      
      // batch 필드 추가
      // 기수(batch) 값이 숫자로 전달되는지 확인
      const batchValue = typeof editStudentFormData.batch === 'number' ? 
        editStudentFormData.batch : 
        typeof editStudentFormData.batch === 'string' ? 
          parseInt(editStudentFormData.batch.replace(/[^0-9]/g, '')) || 1 : 1;
      
      // 항상 기수 포함
      studentData.batch = batchValue;

      // 신규 등록의 경우에만 추가 정보 포함
      if (String(studentId).startsWith('new')) {
        studentData.gender = '남';  // 기본값
        studentData.birthDate = '2000-01-01';  // 기본값
        studentData.address = '';  // 기본값
        studentData.email = `${editStudentFormData.phone}@example.com`;  // 기본값
      }

      console.log('전송할 학생 데이터:', studentData);
      
      // 새 교육생인 경우(id가 'new'로 시작하는 경우)
      if (String(studentId).startsWith('new')) {
        // 새 교육생 API 호출
        console.log('새 교육생 등록 요청:', studentData);
        const response = await authAxios.post('students', studentData);
        console.log('새 교육생 등록 응답:', response.data);
        
        // 교육생 목록 업데이트
        setStudents(prev => [
          ...prev.filter(student => student.id !== studentId),
          {...response.data, id: response.data._id || response.data.id}
        ]);
        
        toast.success(`${studentData.name} 교육생이 추가되었습니다.`);
      } else {
        // 기존 교육생 수정
        console.log(`교육생 ID ${studentId} 수정 요청:`, studentData);
        
        // ID 형식 확인 및 변환
        const formattedId = studentId?.toString();
        if (!formattedId) {
          toast.error('유효하지 않은 교육생 ID입니다.');
          return;
        }
        
        // API 호출
        try {
          const response = await authAxios.put(`students/${formattedId}`, studentData);
          console.log('교육생 수정 응답:', response.data);
        
        // 교육생 목록 업데이트
          const updatedStudents = students.map(student => 
            student.id === studentId ? {...response.data, id: studentId} : student
          );
          
          setStudents(updatedStudents);
          toast.success(`${studentData.name} 교육생 정보가 업데이트되었습니다.`);
        } catch (updateError) {
          console.error('교육생 수정 API 오류:', updateError);
          
          // 상세 오류 정보 로깅 및 표시
          if (updateError.response) {
            console.error('서버 응답 상태:', updateError.response.status);
            console.error('서버 응답 데이터:', updateError.response.data);
            
            const errorMessage = updateError.response.data?.message || 
              updateError.response.statusText || '알 수 없는 오류';
            toast.error(`교육생 수정 실패: ${errorMessage}`);
          } else if (updateError.request) {
            console.error('서버 응답 없음:', updateError.request);
            toast.error('서버 응답이 없습니다. 네트워크 연결을 확인해주세요.');
          } else {
            toast.error(`교육생 수정 오류: ${updateError.message}`);
          }
          
          return; // 오류 발생 시 함수 종료
        }
      }
      
      // 편집 모드 종료 및 폼 데이터 초기화
      setEditingStudentRow(null);
      setEditStudentFormData({
        name: '',
        phone: '',
        email: '',
        gender: '',
        birthDate: '',
        address: '',
        batch: '',
        note: ''
      });
    } catch (err) {
      console.error('학생 인라인 업데이트 오류:', err);
      
      // 오류 유형에 따른 메시지 표시
      if (err.message.includes('Network Error')) {
        toast.error('네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.');
      } else if (err.response && err.response.status === 400) {
        toast.error('입력한 데이터가 유효하지 않습니다. 다시 확인해주세요.');
      } else if (err.response && err.response.status === 404) {
        toast.error('해당 교육생을 찾을 수 없습니다.');
      } else if (err.response && err.response.status === 403) {
        toast.error('이 작업을 수행할 권한이 없습니다.');
      } else {
        toast.error('학생 정보 업데이트 중 오류가 발생했습니다.');
      }
    }
  };
  
  // 빈 회원 데이터 추가
  const addEmptyUser = () => {
    // 임시 ID 생성
    const tempId = `new-${Date.now()}`;
    // 현재 탭에 맞는 기본 역할 설정
    let defaultRoles = [activeTab]; // 현재 활성화된 탭의 역할만 부여
    
    // 빈 회원 데이터 생성
    const newUser = {
      id: tempId,
      name: '',
      username: '',
      email: '',
      roles: defaultRoles
    };
    
    // 회원 목록에 추가
    setUsers([...users, newUser]);
    
    // 바로 편집 모드 활성화
    setEditingRow(tempId);
    setEditFormData({
      name: '',
      username: '',
      email: '',
      roles: defaultRoles
    });
  };
  
  // 빈 교육생 데이터 추가
  const addEmptyStudent = () => {
    // 기존 모달 방식 대신 직접 목록에 빈 데이터 추가
    const tempId = `new-${Date.now()}`;
    const newStudent = {
      id: tempId,
      name: '새 교육생', // 기본 이름 설정
      phone: '',
      email: '',
      batch: 1,
      note: ''
    };
    setStudents([...students, newStudent]);
    setEditingStudentRow(tempId);
    setEditStudentFormData({
      name: '새 교육생', // 기본 이름으로 설정
      phone: '',
      email: '',
      gender: '남',
      birthDate: '',
      address: '',
      batch: 1,
      note: ''
    });
    
    // 약간의 지연 후 전화번호 입력 필드에 포커스
    setTimeout(() => {
      const phoneInput = document.querySelector(`tr[data-student-id="${tempId}"] input[name="phone"]`);
      if (phoneInput) {
        phoneInput.focus();
      }
    }, 100);
  };

  // 역할 토글 핸들러 (인라인 편집용)
  const toggleRole = (role) => {
    setEditFormData(prev => {
      const currentRoles = [...prev.roles];
      if (currentRoles.includes(role)) {
        return { ...prev, roles: currentRoles.filter(r => r !== role) };
      } else {
        return { ...prev, roles: [...currentRoles, role] };
      }
    });
  };

  // 강의 등록 팝업 열기
  const openClassModal = (studentId) => {
    setSelectedStudentId(studentId);
    setSelectedAvailableClasses([]);
    setSelectedEnrolledClasses([]);
    setShowClassModal(true);
  };

  // 강의 등록 팝업 닫기
  const closeClassModal = () => {
    setShowClassModal(false);
    setSelectedStudentId(null);
  };

  // 이용 가능한 강의 목록 계산 (전체 강의에서 이미 수강 중인 강의 제외)
  const getAvailableClasses = () => {
    if (!selectedStudentId || !studentClasses[selectedStudentId]) return [];

    const enrolledClassIds = studentClasses[selectedStudentId].map(cls => cls.id || cls._id);
    return allClasses.filter(cls => !enrolledClassIds.includes(cls.id));
  };

  // 강의 등록하기
  const enrollClasses = async () => {
    if (!selectedStudentId || selectedAvailableClasses.length === 0) return;

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const classId of selectedAvailableClasses) {
        try {
          console.log(`강의 등록 시도 - 강의 ID: ${classId}, 교육생 ID: ${selectedStudentId}`);
          
          // ID 형식 확인
          const formattedClassId = classId?.toString();
          const formattedStudentId = selectedStudentId?.toString();
          
          if (!formattedClassId || !formattedStudentId) {
            console.error('유효하지 않은 ID 형식:', { classId, studentId: selectedStudentId });
            errorCount++;
            errors.push(`강의 ID 또는 교육생 ID가 유효하지 않습니다: ${classId}`);
            continue;
          }

          // 요청 데이터 준비 - 백엔드 DTO에 맞게 형식 지정
          const requestData = { 
            studentId: formattedStudentId,
            classId: formattedClassId 
          };
          console.log(`등록 요청 데이터:`, requestData);
          
          // 백엔드 API 호출
          try {
            const response = await authAxios.post('/attendance/enroll', requestData);
          console.log(`강의 등록 성공 응답:`, response.data);
            
            if (response.data && response.data.success) {
          successCount++;
              console.log(`강의 등록 성공 메시지: ${response.data.message}`);
            } else {
          errorCount++;
              const errorMsg = response.data?.message || '응답에 성공 상태가 없습니다';
              errors.push(`강의 ${classId}: ${errorMsg}`);
              console.warn('API 응답이 성공 상태가 아닙니다:', response.data);
            }
          } catch (apiError) {
            console.error(`API 호출 오류:`, apiError);
            
            // 상세 오류 정보 추출
            let errorMessage = '알 수 없는 오류';
            
            if (apiError.response) {
              const errorData = apiError.response.data;
              // NestJS 오류 형식 처리
              if (errorData?.message) {
                if (Array.isArray(errorData.message)) {
                  errorMessage = errorData.message.join(', ');
          } else {
                  errorMessage = errorData.message;
                }
              } else if (errorData?.error) {
                errorMessage = errorData.error;
              }
              
              console.error(`백엔드 오류 응답:`, {
                statusCode: apiError.response.status,
                data: errorData
              });
            } else if (apiError.message) {
              errorMessage = apiError.message;
            }
            
            errors.push(`강의 ${classId}: ${errorMessage}`);
            errorCount++;
            continue;
          }
        } catch (err) {
          // 이미 처리된 API 오류는 건너뛰기
          if (!errors.some(e => e.includes(`강의 ${classId}`))) {
            errorCount++;
            errors.push(`강의 ${classId}: ${err.message || '알 수 없는 오류'}`);
          }
        }
      }

      // 결과 메시지
      if (successCount > 0) {
        alert(`${successCount}개 강의에 교육생을 등록했습니다.${errorCount > 0 ? `\n${errorCount}개 강의 등록에 실패했습니다.` : ''}`);
      } else if (errorCount > 0) {
        // 상세 오류 정보 표시
        alert(`강의 등록에 실패했습니다.\n\n오류 내용:\n${errors.join('\n')}`);
      }

      // 강의 목록 갱신
      if (successCount > 0) {
        fetchStudentClasses(selectedStudentId);
      }
      
      setSelectedAvailableClasses([]);
    } catch (err) {
      console.error('등록 과정 중 예상치 못한 오류:', err);
      alert('강의 등록 중 오류가 발생했습니다: ' + (err.message || '알 수 없는 오류'));
    }
  };

  // 강의 등록 취소하기
  const unenrollClasses = async () => {
    if (!selectedStudentId || selectedEnrolledClasses.length === 0) return;

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const classId of selectedEnrolledClasses) {
        try {
          console.log(`강의 취소 시도 - 강의 ID: ${classId}, 교육생 ID: ${selectedStudentId}`);
          
          // ID 형식 확인
          const formattedClassId = classId?.toString();
          const formattedStudentId = selectedStudentId?.toString();
          
          if (!formattedClassId || !formattedStudentId) {
            console.error('유효하지 않은 ID 형식:', { classId, studentId: selectedStudentId });
            errorCount++;
            errors.push(`강의 ID 또는 교육생 ID가 유효하지 않습니다: ${classId}`);
            continue;
          }
          
          // 요청 데이터 준비 - 백엔드 DTO에 맞게 형식 지정
          const requestData = { 
              studentId: formattedStudentId,
              classId: formattedClassId
          };
          console.log(`취소 요청 데이터:`, requestData);
          
          // 백엔드 API 호출
          try {
            const response = await authAxios.post('/attendance/unenroll', requestData);
          console.log(`강의 취소 성공 응답:`, response.data);
            
            if (response.data && response.data.success) {
          successCount++;
              console.log(`강의 취소 성공 메시지: ${response.data.message}`);
            } else {
          errorCount++;
              const errorMsg = response.data?.message || '응답에 성공 상태가 없습니다';
              errors.push(`강의 ${classId}: ${errorMsg}`);
              console.warn('API 응답이 성공 상태가 아닙니다:', response.data);
            }
          } catch (apiError) {
            console.error(`API 호출 오류:`, apiError);
            
            // 상세 오류 정보 추출
            let errorMessage = '알 수 없는 오류';
            
            if (apiError.response) {
              const errorData = apiError.response.data;
              // NestJS 오류 형식 처리
              if (errorData?.message) {
                if (Array.isArray(errorData.message)) {
                  errorMessage = errorData.message.join(', ');
          } else {
                  errorMessage = errorData.message;
                }
              } else if (errorData?.error) {
                errorMessage = errorData.error;
              }
              
              console.error(`백엔드 오류 응답:`, {
                statusCode: apiError.response.status,
                data: errorData
              });
            } else if (apiError.message) {
              errorMessage = apiError.message;
            }
            
            errors.push(`강의 ${classId}: ${errorMessage}`);
            errorCount++;
            continue;
          }
        } catch (err) {
          // 이미 처리된 API 오류는 건너뛰기
          if (!errors.some(e => e.includes(`강의 ${classId}`))) {
            errorCount++;
            errors.push(`강의 ${classId}: ${err.message || '알 수 없는 오류'}`);
          }
        }
      }

      // 결과 메시지
      if (successCount > 0) {
        alert(`${successCount}개 강의에서 교육생을 제외했습니다.${errorCount > 0 ? `\n${errorCount}개 강의 취소에 실패했습니다.` : ''}`);
      } else if (errorCount > 0) {
        // 상세 오류 정보 표시
        alert(`강의 취소에 실패했습니다.\n\n오류 내용:\n${errors.join('\n')}`);
      }

      // 강의 목록 갱신
      if (successCount > 0) {
        fetchStudentClasses(selectedStudentId);
      }
      
      setSelectedEnrolledClasses([]);
    } catch (err) {
      console.error('취소 과정 중 예상치 못한 오류:', err);
      alert('강의 취소 중 오류가 발생했습니다: ' + (err.message || '알 수 없는 오류'));
    }
  };

  // 이용 가능한 강의 선택 토글
  const toggleAvailableClass = (classId) => {
    setSelectedAvailableClasses(prev => 
      prev.includes(classId) 
        ? prev.filter(id => id !== classId) 
        : [...prev, classId]
    );
  };

  // 수강 중인 강의 선택 토글
  const toggleEnrolledClass = (classId) => {
    setSelectedEnrolledClasses(prev => 
      prev.includes(classId) 
        ? prev.filter(id => id !== classId) 
        : [...prev, classId]
    );
  };

  // 현재 선택된 기수에 따라 필터링된 교육생 목록 가져오기
  const getFilteredStudents = () => {
    if (batchTab === 'all') {
      return students;
    }
    return students.filter(student => {
      // 학생의 기수 데이터가 문자열일 수도 있고 숫자일 수도 있음
      const studentBatch = typeof student.batch === 'string' 
        ? parseInt(student.batch.replace(/[^0-9]/g, ''))
        : student.batch;
      
      return studentBatch === parseInt(batchTab);
    });
  };

  // 정렬 함수 추가
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // 정렬된 교육생 목록 가져오기
  const getSortedStudents = () => {
    // 먼저 필터링된 교육생 목록 가져오기
    let filteredStudents = getFilteredStudents();
    
    // 정렬 적용
    if (sortConfig.key) {
      filteredStudents.sort((a, b) => {
        // null 체크
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        // 문자열 비교
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredStudents;
  };

  // 교육생 관리 탭 렌더링
  const renderStudentTab = () => {
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">교육생 관리</h2>
          <button
            onClick={addEmptyStudent}
            className="px-3 py-1 bg-green-600 text-white rounded-md text-sm"
          >
            교육생 추가
          </button>
        </div>
        
        {/* 기수별 탭 네비게이션 추가 */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-4">
            <button
              onClick={() => setBatchTab('all')}
              className={`${
                batchTab === 'all'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm`}
            >
              전체
            </button>
            
            {availableBatches.map(batch => (
              <button
                key={batch}
                onClick={() => setBatchTab(batch.toString())}
                className={`${
                  batchTab === batch.toString()
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm`}
              >
                {batch}기
              </button>
            ))}
          </nav>
        </div>
        
        {isLoadingStudents ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : studentError ? (
          <div className="bg-red-100 p-3 rounded-md text-red-700 mb-4">
            {studentError}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="max-h-[calc(100vh-250px)] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('name')}
                    >
                      <div className="flex items-center">
                        이름
                        {sortConfig.key === 'name' && (
                          <span className="ml-1">
                            {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('phone')}
                    >
                      <div className="flex items-center">
                        연락처
                        {sortConfig.key === 'phone' && (
                          <span className="ml-1">
                            {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-bold cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('batch')}
                    >
                      <div className="flex items-center">
                        기수
                        {sortConfig.key === 'batch' && (
                          <span className="ml-1">
                            {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">강의</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">관리</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedStudents().map(student => (
                    <tr key={student.id} data-student-id={student.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingStudentRow === student.id ? (
                          <input
                            type="text"
                            name="name"
                            value={editStudentFormData.name}
                            onChange={handleEditStudentFormChange}
                            className="w-full px-2 py-1 border rounded-md"
                            placeholder="이름 입력"
                            onBlur={() => handleInlineFieldUpdate(student.id, 'name', editStudentFormData.name)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleInlineFieldUpdate(student.id, 'name', editStudentFormData.name)
                                  .then(success => {
                                    if (success) setEditingStudentRow(null);
                                  });
                              }
                            }}
                          />
                        ) : (
                          <span 
                            className="text-sm font-medium cursor-pointer hover:bg-gray-100 p-1 rounded"
                            onClick={() => startInlineEditStudent(student)}
                          >
                            {student.name || '(이름 입력)'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingStudentRow === student.id ? (
                          <input
                            type="text"
                            name="phone"
                            value={editStudentFormData.phone}
                            onChange={handleEditStudentFormChange}
                            className="w-full px-2 py-1 border rounded-md"
                            placeholder="연락처 입력"
                            onBlur={() => handleInlineFieldUpdate(student.id, 'phone', editStudentFormData.phone)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleInlineFieldUpdate(student.id, 'phone', editStudentFormData.phone)
                                  .then(success => {
                                    if (success) setEditingStudentRow(null);
                                  });
                              }
                            }}
                          />
                        ) : (
                          <span 
                            className="text-sm cursor-pointer hover:bg-gray-100 p-1 rounded"
                            onClick={() => startInlineEditStudent(student)}
                          >
                            {student.phone || '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingStudentRow === student.id ? (
                          <input
                            type="number"
                            min="1"
                            step="1"
                            name="batch"
                            value={editStudentFormData.batch}
                            onChange={handleEditStudentFormChange}
                            className="w-full px-2 py-1 border rounded-md"
                            onBlur={() => handleInlineFieldUpdate(student.id, 'batch', editStudentFormData.batch)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleInlineFieldUpdate(student.id, 'batch', editStudentFormData.batch)
                                  .then(success => {
                                    if (success) setEditingStudentRow(null);
                                  });
                              }
                            }}
                          />
                        ) : (
                          <span 
                            className="text-sm font-bold bg-blue-100 text-blue-800 py-1 px-2 rounded-full cursor-pointer"
                            onClick={() => startInlineEditStudent(student)}
                          >
                            {student.batch ? `${student.batch}기` : '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1">
                          <span className="text-sm">
                            {loadingClasses[student.id] ? (
                              <span className="text-gray-400">로딩 중...</span>
                            ) : studentClasses[student.id] && studentClasses[student.id].length > 0 ? (
                              `${studentClasses[student.id].length}개 강의`
                            ) : (
                              <span className="text-gray-400">등록된 강의 없음</span>
                            )}
                          </span>
                          <button
                            onClick={() => openClassModal(student.id)}
                            className="ml-2 text-indigo-600 hover:text-indigo-900 text-xs underline"
                          >
                            강의 관리
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap flex space-x-2">
                        {editingStudentRow === student.id ? (
                          <>
                            <button
                              onClick={() => setEditingStudentRow(null)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              완료
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => onDeleteStudent(student.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              삭제
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* 강의 등록 모달 */}
        {showClassModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-4xl w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">강의 관리</h2>
                <button 
                  onClick={closeClassModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              
              {/* 학생 정보 표시 */}
              {selectedStudentId && (
                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                  <h3 className="font-semibold">
                    {students.find(s => s.id === selectedStudentId)?.name || '선택된 교육생'} - 강의 등록 관리
                  </h3>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                {/* 왼쪽: 이용 가능한 강의 */}
                <div>
                  <h3 className="font-medium mb-2 text-gray-700">이용 가능한 강의</h3>
                  <div className="border rounded-md h-64 overflow-y-auto p-2">
                    {loadingAllClasses ? (
                      <div className="flex justify-center items-center h-full">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                      </div>
                    ) : (
                      <div>
                        <div className="grid grid-cols-12 gap-1 text-xs font-medium text-gray-500 border-b pb-1 mb-1">
                          <div className="col-span-1"></div>
                          <div className="col-span-6">강의명</div>
                          <div className="col-span-3">분류</div>
                          <div className="col-span-2">구성</div>
                        </div>
                        <ul className="divide-y divide-gray-200">
                          {getAvailableClasses().map((cls) => (
                            <li 
                              key={cls.id} 
                              className="grid grid-cols-12 gap-1 py-2 px-1 hover:bg-gray-50 items-center text-sm"
                            >
                              <div className="col-span-1">
                                <input
                                  type="checkbox"
                                  id={`available-class-${cls.id}`}
                                  checked={selectedAvailableClasses.includes(cls.id)}
                                  onChange={() => toggleAvailableClass(cls.id)}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                                />
                              </div>
                              <label 
                                htmlFor={`available-class-${cls.id}`}
                                className="col-span-6 cursor-pointer font-medium"
                              >
                                {cls.name || `강의 #${cls.id}`}
                              </label>
                              <div className="col-span-3 text-xs">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                  {cls.category || '일반'}
                                </span>
                              </div>
                              <div className="col-span-2 text-xs">
                                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full">
                                  {cls.structure || '이론'}
                                </span>
                              </div>
                            </li>
                          ))}
                          {getAvailableClasses().length === 0 && (
                            <li className="py-2 px-1 text-gray-400 text-center">
                              등록 가능한 강의가 없습니다
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={enrollClasses}
                      disabled={selectedAvailableClasses.length === 0}
                      className={`px-3 py-1 rounded-md text-sm ${
                        selectedAvailableClasses.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      선택 강의 등록하기
                    </button>
                  </div>
                </div>
                
                {/* 오른쪽: 이미 등록된 강의 */}
                <div>
                  <h3 className="font-medium mb-2 text-gray-700">수강 중인 강의</h3>
                  <div className="border rounded-md h-64 overflow-y-auto p-2">
                    {loadingClasses[selectedStudentId] ? (
                      <div className="flex justify-center items-center h-full">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                      </div>
                    ) : (
                      <div>
                        <div className="grid grid-cols-12 gap-1 text-xs font-medium text-gray-500 border-b pb-1 mb-1">
                          <div className="col-span-1"></div>
                          <div className="col-span-6">강의명</div>
                          <div className="col-span-3">분류</div>
                          <div className="col-span-2">구성</div>
                        </div>
                        <ul className="divide-y divide-gray-200">
                          {(studentClasses[selectedStudentId] || []).map((cls) => (
                            <li 
                              key={cls.id || cls._id} 
                              className="grid grid-cols-12 gap-1 py-2 px-1 hover:bg-gray-50 items-center text-sm"
                            >
                              <div className="col-span-1">
                                <input
                                  type="checkbox"
                                  id={`enrolled-class-${cls.id || cls._id}`}
                                  checked={selectedEnrolledClasses.includes(cls.id || cls._id)}
                                  onChange={() => toggleEnrolledClass(cls.id || cls._id)}
                                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded mr-2"
                                />
                              </div>
                              <label 
                                htmlFor={`enrolled-class-${cls.id || cls._id}`}
                                className="col-span-6 cursor-pointer font-medium"
                              >
                                {cls.name || `강의 #${cls.id || cls._id}`}
                              </label>
                              <div className="col-span-3 text-xs">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                  {cls.category || '일반'}
                                </span>
                              </div>
                              <div className="col-span-2 text-xs">
                                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full">
                                  {cls.structure || '이론'}
                                </span>
                              </div>
                            </li>
                          ))}
                          {(studentClasses[selectedStudentId] || []).length === 0 && (
                            <li className="py-2 px-1 text-gray-400 text-center">
                              수강 중인 강의가 없습니다
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={unenrollClasses}
                      disabled={selectedEnrolledClasses.length === 0}
                      className={`px-3 py-1 rounded-md text-sm ${
                        selectedEnrolledClasses.length === 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      선택 강의 취소하기
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t flex justify-end">
                <button
                  onClick={closeClassModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">회원 관리</h1>
        <p className="mt-1 text-sm text-gray-500">
          시스템 회원을 관리하고 역할을 할당할 수 있습니다.
        </p>
      </div>

      {/* 2. 탭 네비게이션 버튼 순서 변경 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {/* 교육생 버튼을 첫 번째로 이동 */}
          <button
            onClick={() => setActiveTab('student')}
            className={`${
              activeTab === 'student'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            교육생
          </button>
          {/* 강사 버튼을 두 번째로 이동 */}
          <button
            onClick={() => setActiveTab('instructor')}
            className={`${
              activeTab === 'instructor'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            강사
          </button>
          {/* 관리자 버튼은 그대로 */}
          <button
            onClick={() => setActiveTab('admin')}
            className={`${
              activeTab === 'admin'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            관리자
          </button>
        </nav>
      </div>

      {/* 상단 추가 버튼 제거 - 교육생 탭에서는 필요 없음 */}
      {activeTab !== 'student' && (
      <div className="flex justify-end">
        <button
          type="button"
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={addEmptyUser}
        >
            회원 추가
        </button>
      </div>
      )}

      {/* 회원 추가 폼 */}
      {isAddingUser && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {activeTab === 'student' ? '교육생 추가' : '회원 추가'}
            </h3>
            <form onSubmit={handleSubmit(onAddUser)} className="mt-5 space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  회원 ID
                </label>
                <input
                  type="text"
                  id="username"
                  {...register('username', { 
                    required: '회원 ID는 필수입니다.',
                    pattern: {
                      value: /^[a-zA-Z0-9_]{4,20}$/,
                      message: '회원 ID는 영문, 숫자, 밑줄(_)만 포함하고 4~20자여야 합니다.'
                    }
                  })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.username && (
                  <p className="mt-2 text-sm text-red-600">{errors.username.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  영문, 숫자, 밑줄(_)만 사용하여 4~20자로 입력해주세요. 이미 사용 중인 ID는 사용할 수 없습니다.
                </p>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  비밀번호
                </label>
                <input
                  type="password"
                  id="password"
                  {...register('password', { required: '비밀번호는 필수입니다.' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  이름
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name', { required: '이름은 필수입니다.' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              {/* 이메일 필드 추가 */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  이메일 (선택사항)
                </label>
                <input
                  type="email"
                  id="email"
                  {...register('email')}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  역할
                </label>
                <div className="space-y-2">
                  {ROLES.map((role) => (
                    <div key={role.id} className="relative flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id={`role-${role.id}`}
                          type="checkbox"
                          checked={userRoles[role.id]}
                          onChange={() => setUserRoles(prev => ({ ...prev, [role.id]: !prev[role.id] }))}
                          className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor={`role-${role.id}`} className="font-medium text-gray-700">
                          {role.name}
                        </label>
                        <p className="text-gray-500">{role.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {Object.values(userRoles).every(v => !v) && (
                  <p className="mt-2 text-sm text-red-600">최소한 하나의 역할을 선택해야 합니다.</p>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingUser(false);
                    resetRoles();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 회원 수정 폼 */}
      {isEditingUser && currentUser && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              회원 정보 수정 - {currentUser.name}
            </h3>
            <form onSubmit={handleSubmit(onEditUser)} className="mt-5 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  이름
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name', { required: '이름은 필수입니다.' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  역할
                </label>
                <div className="space-y-2">
                  {ROLES.map((role) => (
                    <div key={role.id} className="relative flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id={`role-${role.id}`}
                          type="checkbox"
                          checked={userRoles[role.id]}
                          onChange={() => setUserRoles(prev => ({ ...prev, [role.id]: !prev[role.id] }))}
                          className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor={`role-${role.id}`} className="font-medium text-gray-700">
                          {role.name}
                        </label>
                        <p className="text-gray-500">{role.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {Object.values(userRoles).every(v => !v) && (
                  <p className="mt-2 text-sm text-red-600">최소한 하나의 역할을 선택해야 합니다.</p>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingUser(false);
                    setCurrentUser(null);
                    resetRoles();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 비밀번호 재설정 폼 */}
      {isResettingPassword && currentUser && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              비밀번호 재설정 - {currentUser.name}
            </h3>
            <form onSubmit={handleSubmit(onResetPassword)} className="mt-5 space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  새 비밀번호
                </label>
                <input
                  type="password"
                  id="newPassword"
                  {...register('newPassword', { 
                    required: '새 비밀번호는 필수입니다.',
                    minLength: { value: 6, message: '비밀번호는 최소 6자 이상이어야 합니다.' } 
                  })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.newPassword && (
                  <p className="mt-2 text-sm text-red-600">{errors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  비밀번호 확인
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  {...register('confirmPassword', { 
                    required: '비밀번호 확인은 필수입니다.',
                    validate: value => value === document.getElementById('newPassword').value || '비밀번호가 일치하지 않습니다.'
                  })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  비밀번호 변경
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsResettingPassword(false);
                    setCurrentUser(null);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 교육생 추가 폼 */}
      {isAddingStudent && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              새 교육생 추가
            </h3>
            <form onSubmit={handleSubmit(onAddStudent)} className="mt-5 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  이름
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name', { required: '이름은 필수입니다.' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  핸드폰 번호
                </label>
                <input
                  type="text"
                  id="phone"
                  {...register('phone', { 
                    required: '핸드폰 번호는 필수입니다.',
                    pattern: {
                      value: /^[0-9]{10,11}$/,
                      message: '유효한 핸드폰 번호를 입력해주세요.'
                    }
                  })}
                  placeholder="01012345678 (- 없이 입력)"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="note" className="block text-sm font-medium text-gray-700">
                  비고 (선택사항)
                </label>
                <textarea
                  id="note"
                  {...register('note')}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingStudent(false);
                    reset();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 교육생 수정 폼 */}
      {isEditingStudent && currentStudent && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              교육생 정보 수정 - {currentStudent.name}
            </h3>
            <form onSubmit={handleSubmit(onEditStudent)} className="mt-5 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  이름
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name', { required: '이름은 필수입니다.' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  핸드폰 번호
                </label>
                <input
                  type="text"
                  id="phone"
                  {...register('phone', { 
                    required: '핸드폰 번호는 필수입니다.',
                    pattern: {
                      value: /^[0-9]{10,11}$/,
                      message: '유효한 핸드폰 번호를 입력해주세요.'
                    }
                  })}
                  placeholder="01012345678 (- 없이 입력)"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="note" className="block text-sm font-medium text-gray-700">
                  비고 (선택사항)
                </label>
                <textarea
                  id="note"
                  {...register('note')}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingStudent(false);
                    setCurrentStudent(null);
                    reset();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 교육생 목록 */}
      {activeTab === 'student' && (
        renderStudentTab()
      )}

      {/* 회원 목록 */}
      {activeTab !== 'student' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="px-4 py-5 sm:px-6">
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            </div>
          ) : getFilteredUsers().length === 0 ? (
            <div className="px-4 py-5 sm:p-6 text-center text-gray-500">
              {activeTab === 'instructor' ? '등록된 강사가 없습니다.' : 
               activeTab === 'admin' ? '등록된 관리자가 없습니다.' :
               '등록된 교육생이 없습니다.'}
              새 회원을 추가해주세요.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      이름
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      회원 ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      역할
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      관리
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredUsers().map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingRow === user.id ? (
                          <input
                            type="text"
                            name="name"
                            value={editFormData.name}
                            onChange={handleEditFormChange}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="이름 입력"
                          />
                        ) : (
                          <div 
                            className="text-sm font-medium text-indigo-600 cursor-pointer hover:bg-gray-100 p-1 rounded"
                            onClick={() => startInlineEdit(user)}
                          >
                            {user.name || '(이름 입력)'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingRow === user.id && String(user.id).startsWith('new') ? (
                          <input
                            type="text"
                            name="username"
                            value={editFormData.username}
                            onChange={handleEditFormChange}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="회원 ID 입력"
                          />
                        ) : (
                          <div className="text-sm text-gray-900">
                            {user.username || '(ID 미설정)'}
                            {activeTab === 'student' && user.studentId && (
                              <div className="text-xs text-gray-500">학번: {user.studentId}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingRow === user.id ? (
                          <div className="flex flex-wrap gap-2">
                            {ROLES.map((role) => (
                              <div key={role.id} className="flex items-center">
                                <input
                                  type="checkbox"
                                  id={`role-${user.id}-${role.id}`}
                                  checked={editFormData.roles.includes(role.id)}
                                  onChange={() => toggleRole(role.id)}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <label htmlFor={`role-${user.id}-${role.id}`} className="ml-2 text-sm text-gray-700">
                                  {role.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {user.roles.map((role) => (
                              <span
                                key={role}
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(role)}`}
                              >
                                {getRoleDisplayName(role)}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {editingRow === user.id ? (
                          <div className="flex justify-end space-x-2">
                            <button
                              type="button"
                              onClick={() => handleInlineUpdate(user.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              저장
                            </button>
                            <button
                              type="button"
                              onClick={cancelInlineEdit}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              취소
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end space-x-2">
                            {!String(user.id).startsWith('new') && (
                              <button
                                type="button"
                                onClick={() => startResetPassword(user)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                비밀번호 재설정
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => startInlineEdit(user)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              수정
                            </button>
                            <button
                              type="button"
                              onClick={() => onDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              삭제
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserManagement; 